import { State, Action, StateContext, Selector } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { AuthActions } from './auth.actions';
import { AuthResponse, AuthStateModel, OTP_MEANS, PreAuthResponse, ProfileModel } from './auth.model';
import { catchError, tap } from 'rxjs/operators';
import { AuthService } from '@tsin-core/services/auth.service';


const noAuth = {
  id: null,
  email: null,
  user: null,
  token: null,
  loading: false,
  skipValidation: false,
  refreshToken: null,
  requiresMfa: false,
  mfaMethod: "None" as "None" | "Email" | "App" | "Passkey",
  isGeneratedPassword: false,
  profiles: [],
  referralJobSlug: null,
  secret: null,
  qrText: null,
}
@State<AuthStateModel>({
  name: 'authState',
  defaults: {
    loading: false,
    preAuthConfig: null,
    auth: noAuth,
    error: null,
    referralJobSlug: null,
    isOtpValidated: false,
    secret: null,
    qrText: null,
    passkeys: [],
    passkeySetupInProgress: false,
    passkeyResponse: null,
    passkeyAuthResponse: null
  },
})

@Injectable()
export class AuthState {
  authService: AuthService = inject(AuthService);

  @Selector()
  static getUser(state: AuthStateModel): any {
    return state.auth.user;
  }

  @Selector()
  static getProfiles(state: AuthStateModel): ProfileModel[] {
    return state.auth.user?.profiles || [];
  }

  @Selector()
  static getEmail(state: AuthStateModel): string {
    return state.auth.user?.email ?? '';
  }

  @Selector()
  static getIsGeneratedPassword(state: AuthStateModel): any {
    return state.auth.isGeneratedPassword;
  }

  @Selector()
  static getToken(state: AuthStateModel): string | null {
    return state.auth.token;
  }

  @Selector()
  static getRefreshToken(state: AuthStateModel): string | null {
    return state.auth.refreshToken;
  }


  @Selector()
  static isLoading(state: AuthStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static isOtpValidated(state: AuthStateModel): boolean {
    return state.isOtpValidated;
  }

  @Selector()
  static isOtpEnabled(state: AuthStateModel): boolean {
    return state.auth.requiresMfa;
  }

  @Selector()
  static getPreAuthConfig(state: AuthStateModel): PreAuthResponse | null {
    return state.preAuthConfig;
  }

  @Selector()
  static referralJobSlug(state: AuthStateModel): string | null {
    return state.referralJobSlug;
  }

  @Selector()
  static getAuth(state: AuthStateModel): AuthResponse {
    return state.auth;
  }

  @Selector()
  static otpMeans(state: AuthStateModel): OTP_MEANS {
    return state.auth.user?.otpMeans ?? OTP_MEANS.none;
  }

  @Selector()
  static getPasskeys(state: AuthStateModel): any[] {
    return state.passkeys;
  }

  @Selector()
  static getPasskeySetupInProgress(state: AuthStateModel): boolean {
    return state.passkeySetupInProgress;
  }

  @Action(AuthActions.Signup)
  signup(ctx: StateContext<AuthStateModel>, action: AuthActions.Signup) {
    ctx.patchState({ loading: true });
    return this.authService.signup(action.payload).pipe(
      tap((response) => {
        const state = ctx.getState();
        ctx.patchState({ auth: { ...state.auth, user: response }, loading: false });
      })
    );
  }

  @Action(AuthActions.Login)
  login(ctx: StateContext<AuthStateModel>, action: AuthActions.Login) {
    ctx.patchState({ loading: true });
    return this.authService.login(action.payload).pipe(
      tap((response) => {
        console.log(response);
        ctx.patchState({
          auth: { ...response, requiresMfa: response.skipValidation ? true : response.requiresMfa },
          isOtpValidated: response.skipValidation == true ? true : false,
          loading: false
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.RefreshToken)
  refreshTokenReq(ctx: StateContext<AuthStateModel>, action: AuthActions.RefreshToken) {
    ctx.patchState({ loading: true });
    return this.authService.refreshToken(action.payload).pipe(
      tap((response: { accessToken: string, expireIn: number }) => {
        ctx.patchState({ auth: { ...ctx.getState().auth, token: response.accessToken }, loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.ContinueWithEmail)
  continueWithEmail(ctx: StateContext<AuthStateModel>, action: AuthActions.ContinueWithEmail) {
    ctx.patchState({ loading: true });
    return this.authService.validateWithEmail(action.payload).pipe(
      tap((response) => {
        // console.log(response);
        ctx.patchState({
          preAuthConfig: response,
          loading: false
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.PasskeyAuthenticationOptions)
  passkeyAuthenticationOptions(ctx: StateContext<AuthStateModel>, action: AuthActions.PasskeyAuthenticationOptions) {
    ctx.patchState({ loading: true });
    return this.authService.passkeyAuthenticationOptions(action.payload).pipe(
      tap((response) => {
        ctx.patchState({ loading: false, passkeyAuthResponse: response });
        console.log(response);
        // ctx.patchState({ preAuthConfig: response, loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.PasskeyRegistrationOptions)
  passkeyRegistrationOptions(ctx: StateContext<AuthStateModel>, action: AuthActions.PasskeyRegistrationOptions) {
    ctx.patchState({ loading: true });
    return this.authService.passkeyRegistrationOptions(action.payload).pipe(
      tap((response) => {
        console.log('PassKey ', response);
        ctx.patchState({ passkeySetupInProgress: true, passkeyResponse: response, loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, passkeySetupInProgress: false, });
        throw error;
      })
    );
  }

  @Action(AuthActions.PasskeyRegistrationVerify)
  passkeyRegistrationVerify(ctx: StateContext<AuthStateModel>, action: AuthActions.PasskeyRegistrationVerify) {
    ctx.patchState({ loading: true });
    return this.authService.passkeyRegistrationVerify(action.payload).pipe(
      tap((response) => {
        ctx.patchState({ passkeySetupInProgress: false, loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ passkeySetupInProgress: false, loading: false });
        throw error;
      })
    );
  }


  @Action(AuthActions.PasskeyLogin)
  passkeyLogin(ctx: StateContext<AuthStateModel>, action: AuthActions.PasskeyLogin) {
    ctx.patchState({ loading: true });
    return this.authService.passkeyLogin(action.payload).pipe(
      tap((response: any) => {
        ctx.patchState({
          auth: { ...response, isOtpEnabled: true },
          isOtpValidated: true,
          loading: false
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.LoginWWU)
  loginWWU(ctx: StateContext<AuthStateModel>, action: AuthActions.LoginWWU) {
    ctx.patchState({ loading: true });
    return this.authService.loginWWU(action.payload).pipe(
      tap((response) => {
        console.log(response);
        ctx.patchState({
          auth: { ...response, requiresMfa: response.skipValidation ? true : response.requiresMfa },
          isOtpValidated: response.skipValidation == true ? true : false,
          loading: false
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.ChangePassword)
  changePassword(ctx: StateContext<AuthStateModel>, action: AuthActions.ChangePassword) {
    ctx.patchState({ loading: true });
    return this.authService.changePassword(action.payload).pipe(
      tap((response) => {
        const state = ctx.getState();
        console.log('Response', response);
        ctx.patchState({ auth: { ...state.auth, isGeneratedPassword: false }, loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.Logout)
  logout(ctx: StateContext<AuthStateModel>) {
    // Reset state to defaults
    ctx.setState({
      loading: false,
      preAuthConfig: null,
      auth: noAuth,
      error: null,
      referralJobSlug: null,
      isOtpValidated: false,
      secret: null,
      qrText: null,
      passkeys: [],
      passkeySetupInProgress: false,
      passkeyResponse: null,
      passkeyAuthResponse: null
    });
  }

  @Action(AuthActions.SetReferralJobSlug)
  setReferralJobSlug(ctx: StateContext<AuthStateModel>, action: AuthActions.SetReferralJobSlug) {
    // Reset state to defaults
    ctx.patchState({
      referralJobSlug: action.payload!
    });
  }

  @Action(AuthActions.SendForgotPasswordRequest)
  sendForgotPasswordRequest(ctx: StateContext<AuthStateModel>, action: AuthActions.SendForgotPasswordRequest) {
    ctx.patchState({ loading: true });
    return this.authService.sendForgotPasswordRequest(action.payload).pipe(
      tap(() => {
        ctx.patchState({ loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.VerifyForgotPasswordRequest)
  verifyForgotPasswordRequest(ctx: StateContext<AuthStateModel>, action: AuthActions.VerifyForgotPasswordRequest) {
    ctx.patchState({ loading: true });
    return this.authService.verifyForgotPasswordRequest(action.payload).pipe(
      tap(() => {
        ctx.patchState({ loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }


  @Action(AuthActions.ResendVerificationEmail)
  resendVerificationEmail(ctx: StateContext<AuthStateModel>, action: AuthActions.ResendVerificationEmail) {
    ctx.patchState({ loading: true });
    return this.authService.resendVerificationEmail(action.payload).pipe(
      tap(() => {
        ctx.patchState({ loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.GenerateOtp)
  generateOtp(ctx: StateContext<AuthStateModel>, action: AuthActions.GenerateOtp) {
    ctx.patchState({ loading: true });
    return this.authService.generateOtp(action.payload).pipe(
      tap((response) => {
        ctx.patchState({ loading: false, qrText: response.otpAuthUrl, secret: response.secret });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.GenerateOtpWWU)
  generateOtpWWU(ctx: StateContext<AuthStateModel>, action: AuthActions.GenerateOtpWWU) {
    ctx.patchState({ loading: true });
    return this.authService.generateOtpWWU(action.payload).pipe(
      tap((response) => {
        //
        console.log(response);
        ctx.patchState({ loading: false, qrText: response.otpAuthUrl, secret: response.secret });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.VerifyOtp)
  verifyOtp(ctx: StateContext<AuthStateModel>, action: AuthActions.VerifyOtp) {
    ctx.patchState({ loading: true });
    return this.authService.verifyOtp(action.payload).pipe(
      tap((response) => {
        console.log('Response', response);
        ctx.patchState({ isOtpValidated: true, loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.VerifyOtpWWU)
  verifyOtpWWU(ctx: StateContext<AuthStateModel>, action: AuthActions.VerifyOtpWWU) {
    ctx.patchState({ loading: true });
    return this.authService.verifyOtpWWU(action.payload).pipe(
      tap((response: any) => {
        ctx.patchState({
          isOtpValidated: true,
          loading: false
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }



  @Action(AuthActions.ValidateOtp)
  validateOtp(ctx: StateContext<AuthStateModel>, action: AuthActions.ValidateOtp) {
    ctx.patchState({ loading: true });
    return this.authService.validateOtp(action.payload).pipe(
      tap((response) => {
        ctx.patchState({ auth: response, isOtpValidated: true, loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }


  @Action(AuthActions.ValidateOtpWWU)
  validateOtpWWU(ctx: StateContext<AuthStateModel>, action: AuthActions.ValidateOtpWWU) {
    ctx.patchState({ loading: true });
    return this.authService.ValidateOtpWWU(action.payload).pipe(
      tap((response) => {
        console.log('Validate WWU', response);
        ctx.patchState({ auth: response, isOtpValidated: true, loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.DisableOtp)
  disableOtp(ctx: StateContext<AuthStateModel>, action: AuthActions.DisableOtp) {
    ctx.patchState({ loading: true });
    return this.authService.disableOtp(action.payload).pipe(
      tap(() => {
        ctx.patchState({ loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.GetMe)
  getMe(ctx: StateContext<AuthStateModel>) {
    ctx.patchState({ loading: true });
    return this.authService.getMe().pipe(
      tap((response) => {
        // ctx.patchState({ user: response, loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.EnableEmail2FA)
  enableEmail2FA(ctx: StateContext<AuthStateModel>, action: AuthActions.EnableEmail2FA) {
    ctx.patchState({ loading: true });
    return this.authService.enableEmail2FA(action.id).pipe(
      tap(() => {
        ctx.patchState({ loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }


  @Action(AuthActions.EnableEmail2FAWWU)
  enableEmail2FAWWU(ctx: StateContext<AuthStateModel>, action: AuthActions.EnableEmail2FAWWU) {
    ctx.patchState({ loading: true });
    return this.authService.enableEmail2FAWWU(action.id).pipe(
      tap((response) => {
        console.log(response);
        ctx.patchState({ loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.ConfirmEmail2FA)
  confirmEmail2FA(ctx: StateContext<AuthStateModel>, action: AuthActions.ConfirmEmail2FA) {
    ctx.patchState({ loading: true });
    return this.authService.confirmEmail2FA(action.otp).pipe(
      tap((response) => {
        ctx.patchState({ auth: response, isOtpValidated: true, loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.ConfirmEmail2FAWWU)
  confirmEmail2FAWWU(ctx: StateContext<AuthStateModel>, action: AuthActions.ConfirmEmail2FA) {
    ctx.patchState({ loading: true });
    return this.authService.confirmEmail2FAWWU(action.otp).pipe(
      tap((response) => {
        ctx.patchState({ auth: response, isOtpValidated: true, loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.VerifyEmail)
  verifyEmail(ctx: StateContext<AuthStateModel>, action: AuthActions.VerifyEmail) {
    ctx.patchState({ loading: true });
    return this.authService.verifyEmail(action.payload).pipe(
      tap((response) => {
        console.log(response);
        ctx.patchState({ loading: false });
      }),
      catchError((error) => {
        console.warn(error);
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }


  @Action(AuthActions.VerifyEmailWWU)
  verifyEmailWWU(ctx: StateContext<AuthStateModel>, action: AuthActions.VerifyEmailWWU) {
    ctx.patchState({ loading: true });
    return this.authService.verifyEmailWWU(action.payload).pipe(
      tap((response) => {
        console.log(response);
        ctx.patchState({ loading: false });
      }),
      catchError((error) => {
        console.warn(error);
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }


  @Action(AuthActions.CreateAccountWorkWithUs)
  createAccountWorkWithUs(ctx: StateContext<AuthStateModel>, action: AuthActions.CreateAccountWorkWithUs) {
    ctx.patchState({ loading: true });
    return this.authService.signUpWorkWithUs(action.payload).pipe(
      tap((response) => {
        console.log('Response', response);
        ctx.patchState({ loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        console.log(error);
        throw error;
      })
    );
  }

  // Passkey Actions
  @Action(AuthActions.StartPasskeySetup)
  startPasskeySetup(ctx: StateContext<AuthStateModel>, action: AuthActions.StartPasskeySetup) {
    ctx.patchState({ passkeySetupInProgress: true, loading: true });
    return this.authService.startPasskeyRegistration(action.passkeyName).pipe(
      tap((response) => {
        ctx.patchState({ loading: false });
        // The response should contain registration options for WebAuthn
        return response;
      }),
      catchError((error) => {
        ctx.patchState({ passkeySetupInProgress: false, loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.CompletePasskeyRegistration)
  completePasskeyRegistration(ctx: StateContext<AuthStateModel>, action: AuthActions.CompletePasskeyRegistration) {
    ctx.patchState({ loading: true });
    return this.authService.completePasskeyRegistration(action.payload).pipe(
      tap((response) => {
        const state = ctx.getState();
        ctx.patchState({
          passkeySetupInProgress: false,
          loading: false,
          passkeys: [...state.passkeys, response.passkey]
        });
      }),
      catchError((error) => {
        ctx.patchState({ passkeySetupInProgress: false, loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.LoadUserPasskeys)
  loadUserPasskeys(ctx: StateContext<AuthStateModel>) {
    ctx.patchState({ loading: true });
    return this.authService.getUserPasskeys().pipe(
      tap((passkeys) => {
        ctx.patchState({ passkeys, loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }

  @Action(AuthActions.DeletePasskey)
  deletePasskey(ctx: StateContext<AuthStateModel>, action: AuthActions.DeletePasskey) {
    ctx.patchState({ loading: true });
    return this.authService.deletePasskey(action.passkeyId).pipe(
      tap(() => {
        const state = ctx.getState();
        const updatedPasskeys = state.passkeys.filter(pk => pk.id !== action.passkeyId);
        ctx.patchState({ passkeys: updatedPasskeys, loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        throw error;
      })
    );
  }

}
